import classNames from 'classnames';
import { ChangeEventHandler, FormEventHandler, FunctionComponent, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import CrossIcon from '@/app/components/CrossIcon/CrossIcon';
import LensIcon from '@/app/components/LensIcon/LensIcon';

export interface SearchBarFormValues {
  searchTerm: string;
}

interface SearchBarProps {
  isOpen: boolean;
  searchTerm: string;
  onSearch: (searchTerm: string) => void;
  onChange: (searchTerm: string) => void;
  onClose: () => void;
}

const SearchBar: FunctionComponent<SearchBarProps> = ({ isOpen, searchTerm, onChange, onSearch, onClose }) => {
  const { t } = useTranslation();
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (event) => {
      event.preventDefault();
      onSearch(searchTerm);
    },
    [onSearch, searchTerm],
  );

  const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      onChange(event?.target.value);
    },
    [onChange],
  );

  useEffect(() => {
    if (isOpen) {
      searchInputRef.current?.focus();
    }
  }, [isOpen]);

  return (
    <div
      className={classNames(
        'absolute w-full h-[64px] lg:h-[96px] left-0 border-b-2 border-primary-light transition-all duration-500',
        {
          'top-0 bg-white': isOpen,
          'top-[-96px] bg-primary-light': !isOpen,
        },
      )}
    >
      <div className="default-page-max-width h-full my-0 mx-auto flex items-center justify-between px-[24px] lg:px-0">
        <form onSubmit={handleSubmit}>
          <div className="flex items-center w-full">
            <LensIcon className="text-primary-light" />
            <input
              ref={searchInputRef}
              type="text"
              name="searchTerm"
              onChange={handleChange}
              value={searchTerm ?? ''}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              placeholder={t('components.SearchBar.placeholder')}
              className="py-[12px] px-[24px] !border-none !outline-none placeholder-neutral-mid placeholder:italic shrink-0 grow bg-transparent"
            />
          </div>
        </form>
        <button className="block hover:text-primary-mid" onClick={onClose}>
          <CrossIcon />
        </button>
      </div>
    </div>
  );
};

export default SearchBar;
